import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import { GlobalApp } from "../../tools/global";
import { Subscription } from "dexie";
import { UserState } from "../../api/models/user-state";
import { SubscriptionService } from "../../api/services/subscription.service";
import { AdmLayoutService } from "../../api/services/adm-layout.service";
import { AdmLayoutEx } from "../../api/models/adm-layout-ex";
import { AdmSubscriptionLite } from "../../api/models/adm-subscription-lite";
import { AdmLayoutDialogComponent } from "../../dialogs/adm-layout-dialog/adm-layout-dialog.component";
import { AdmConnectionDialogComponent } from "../../dialogs/adm-connection-dialog/adm-connection-dialog.component";
import { ConfirmDialogComponent } from "../../dialogs/confirm-dialog/confirm-dialog.component";
import { DialogResultType } from "../../dialogs/DialogResult";

export interface AdmLayoutDetails extends AdmLayoutEx {
  statusText: string;
}

// AdmInstanceStatus is generated as a plain numeric union, not a runtime enum - these mirror its C# member order
// exactly (PublicInsight.Db/Models/AdmInstance.cs).
const ADM_STATUS = {
  Provisioning: 0, Loading: 1, Verifying: 2, Active: 3, Failed: 4, Superseded: 5, PendingCleanup: 6, Purged: 7,
} as const;

// "My ADMs" - the list page for Agent Data Models. Polls for status; supports edit/copy/delete/regenerate/connection actions.
@Component({
  selector: "app-adm-layouts",
  templateUrl: "./adm-layouts.component.html",
})
export class AdmLayoutsComponent implements OnInit, OnDestroy {
  userState: UserState = null;
  userStateSubscription: Subscription = null;

  public loadingData = true;
  public subscriptions: AdmSubscriptionLite[] = [];
  // Loads independently of, and usually slower than, currentLayouts (AvailableSubscriptions does real
  // entitlement work - plan type -> Groups -> allowlist - while an empty MyLayouts can come back almost
  // instantly). Gate the "not entitled" empty-state text on this too, so it cannot flash false before
  // subscriptions has actually loaded (it used to read subscriptions.length === 0 as soon as loadingData
  // went false from MyLayouts alone, which is true and wrong for that brief window).
  public subscriptionsLoaded = false;
  public currentLayouts: AdmLayoutDetails[] = [];
  public refreshTimer: any = null;
  private refreshingStatusIds = new Set<number>();
  public isAdmin = false;

  constructor(
    private app: GlobalApp,
    private router: Router,
    private subscriptionService: SubscriptionService,
    private admLayoutService: AdmLayoutService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.app.getAuthorizationStatus().subscribe((result) => {
      if (!result.isValid) {
        this.app.logOut();
        this.router.navigate(["login"]);
      } else {
        this.isAdmin = !!result.isAdmin;
        this.app.initialRefreshUserState(this.subscriptionService);
      }
    });
  }

  ngOnInit() {
    this.userStateSubscription = this.app.userState.subscribe((userState) => {
      this.userState = userState;
    });

    this.admLayoutService.admLayoutGetAvailableSubscriptions().subscribe({
      next: (subscriptions) => (this.subscriptions = subscriptions || []),
      complete: () => (this.subscriptionsLoaded = true),
      error: () => (this.subscriptionsLoaded = true),
    });

    this.refreshTimer = setInterval(() => this.refreshStatuses(), 5000);
    this.loadLayouts();
  }

  ngOnDestroy() {
    if (this.userStateSubscription) this.userStateSubscription.unsubscribe();
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  loadLayouts() {
    this.loadingData = true;
    this.admLayoutService.admLayoutGetMyLayouts().subscribe((layouts) => {
      this.currentLayouts = (layouts || []).map((x) => ({ ...x, statusText: this.getStatusText(x) }));
      this.loadingData = false;
    });
  }

  // Polls each layout's latest instance so a Regenerate's real outcome appears without a page reload.
  refreshStatuses() {
    this.currentLayouts.forEach((layout) => {
      // Skip layouts whose previous status request has not completed yet, so slow responses never pile up.
      if (this.refreshingStatusIds.has(layout.admLayoutId)) return;
      this.refreshingStatusIds.add(layout.admLayoutId);
      this.admLayoutService.admLayoutGetLayoutStatus(layout.admLayoutId).pipe(
        finalize(() => this.refreshingStatusIds.delete(layout.admLayoutId))
      ).subscribe((latest) => {
        if (!latest) return;
        layout.status = latest.status;
        layout.lastRefreshedUtc = latest.lastRefreshedUtc;
        layout.errorMessage = latest.errorMessage;
        layout.hasCurrentInstance = latest.hasCurrentInstance;
        layout.queuePosition = latest.queuePosition;
        layout.queueLength = latest.queueLength;
        layout.estimatedMinutes = latest.estimatedMinutes;
        layout.statusText = this.getStatusText(layout);
      });
    });
  }

  // Layouts whose Regenerate request is in flight: it can take a while to be accepted and show as Queued, so
  // the row says so straight away rather than looking like nothing happened.
  public startingIds = new Set<number>();

  getStatusText(layout: AdmLayoutEx): string {
    if (this.startingIds.has(layout.admLayoutId)) return "Starting regeneration...";
    const base = this.getBaseStatusText(layout);
    // An automatic refresh waiting in line leaves the layout showing its previous (Active) status.
    if (layout.queuePosition && layout.status !== ADM_STATUS.Provisioning) return `${base} - Update ${this.getQueueText(layout)}`;
    return base;
  }

  private getBaseStatusText(layout: AdmLayoutEx): string {
    if (layout.status === undefined || layout.status === null) return "Never Generated";
    const updated = layout.lastRefreshedUtc ? new Date(layout.lastRefreshedUtc + "Z").toLocaleString() : "Unknown";
    switch (layout.status) {
      case ADM_STATUS.Provisioning: return layout.queuePosition ? `Queued ${this.getQueueText(layout, false)}` : "Queued...";
      case ADM_STATUS.Loading: return `Building (reading source data)...${this.getEstimateText(layout, "remaining")}`;
      case ADM_STATUS.Verifying: return `Building (loading and verifying)...${this.getEstimateText(layout, "remaining")}`;
      case ADM_STATUS.Active: return `Active - Last Refreshed: ${updated}`;
      case ADM_STATUS.Failed:
        return layout.hasCurrentInstance
          ? `Refresh failed, previous version still serving: ${layout.errorMessage || "Unknown error"}`
          : `Failed: ${layout.errorMessage || "Unknown error"}`;
      case ADM_STATUS.Superseded: return layout.hasCurrentInstance ? `Active - Last Refreshed: ${updated}` : "Superseded";
      case ADM_STATUS.PendingCleanup:
      case ADM_STATUS.Purged: return "Removed (idle) - Regenerate to bring it back";
      default: return "Unknown";
    }
  }

  private getQueueText(layout: AdmLayoutEx, prefix = true): string {
    const place = layout.queuePosition === 1 ? "next in line" : `position ${layout.queuePosition} of ${layout.queueLength}`;
    return `${prefix ? "queued, " : ""}${prefix ? place : `- ${place}`}${this.getEstimateText(layout, "until ready")}`;
  }

  private getEstimateText(layout: AdmLayoutEx, suffix: string): string {
    if (layout.estimatedMinutes === undefined || layout.estimatedMinutes === null) return "";
    const minutes = Math.max(1, Math.round(layout.estimatedMinutes));
    const text = minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
    return ` (about ${text} ${suffix})`;
  }

  // Subscriptions a NEW layout may be created under: any subscription an admin can see (an admin may bookkeep a
  // curated Subscription ADM under one that only consumes), or - for everyone else - only ones that can
  // actually self-service create (a consume-only subscription cannot own a new self-created layout).
  get creatableSubscriptions(): AdmSubscriptionLite[] {
    return this.isAdmin ? this.subscriptions : this.subscriptions.filter((s) => s.canSelfServiceCreate);
  }

  // Opens the layout dialog and drives its save. A rejected save (entitlement, missing filtering, ...) reopens the
  // dialog with the server's message so the user's work is not lost.
  private openLayoutDialog(
    layout: AdmLayoutEx,
    save: (data: AdmLayoutEx) => Observable<any>,
    onSaved: () => void,
    initialErrorMessage?: string
  ) {
    AdmLayoutDialogComponent.showDialog(this.dialog, {
      layout,
      subscriptions: this.creatableSubscriptions,
      preFilters: (this.userState && this.userState.preFilters) || [],
      initialErrorMessage,
      isAdmin: this.isAdmin,
    }).subscribe((result) => {
      if (!result || result.ResultType !== DialogResultType.Ok) return;

      this.app.pushLoading("Saving, Please Wait...");
      save(result.ResultData).subscribe({
        next: () => {
          this.app.popLoading();
          onSaved();
        },
        error: (error) => {
          this.app.popLoading();
          const message = (error && (error.error || error.message)) || "Failed to save the Agent Data Model.";
          this.snackBar.open(message, null, { duration: 8000 });
          this.openLayoutDialog(result.ResultData, save, onSaved, message);
        },
      });
    });
  }

  createLayout() {
    const layout: AdmLayoutEx = {
      admLayoutId: 0,
      subscriptionId: this.creatableSubscriptions.length > 0 ? this.creatableSubscriptions[0].subscriptionId : 0,
      name: "",
      preFilterId: null,
      directFilterData: "{}",
      isSubscriptionAdm: false,
      selectedColumnIds: [],
      hasCurrentInstance: false,
      queueLength: 0,
      canManage: true,
    };
    this.openLayoutDialog(layout, (data) => this.admLayoutService.admLayoutCreateLayout(data), () => this.loadLayouts());
    return false;
  }

  copyLayout(layout: AdmLayoutEx) {
    const copy: AdmLayoutEx = { ...layout, admLayoutId: 0, name: `${layout.name} (Copy)`, hasCurrentInstance: false, status: undefined };
    this.openLayoutDialog(copy, (data) => this.admLayoutService.admLayoutCreateLayout(data), () => this.loadLayouts());
    return false;
  }

  editLayout(layout: AdmLayoutEx) {
    const layoutId = layout.admLayoutId;
    this.openLayoutDialog(
      { ...layout },
      (data) => this.admLayoutService.admLayoutUpdateLayout({ layoutId, request: data }),
      () => {
        this.loadLayouts();
        this.promptRegenerate(layoutId);
      }
    );
    return false;
  }

  private promptRegenerate(layoutId: number) {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Regenerate Agent Data Model?",
      "Saved. Regenerate now? The current version (if any) keeps serving until a regenerate succeeds."
    ).subscribe((result) => {
      if (result.ResultType === DialogResultType.Ok) this.regenerateLayout(layoutId);
    });
  }

  regenerateLayout(layoutId: number) {
    const current = this.currentLayouts?.find((l) => l.admLayoutId === layoutId);
    if (this.startingIds.has(layoutId)) return false;
    if (current && current.status === ADM_STATUS.Provisioning) {
      this.snackBar.open("Already queued - see status below", null, { duration: 4000 });
      return false;
    }
    this.setStarting(current, true);
    this.snackBar.open("Starting Regeneration...", null, { duration: 3000 });
    this.admLayoutService.admLayoutRegenerate(layoutId).subscribe({
      next: () => {
        // Show it as queued right away; the next poll fills in position and estimate.
        if (current) { current.status = ADM_STATUS.Provisioning; current.queuePosition = undefined; current.estimatedMinutes = undefined; }
        this.setStarting(current, false);
        this.snackBar.open("Regeneration started - see status below", null, { duration: 5000 });
        this.refreshStatuses();
      },
      error: (error) => {
        this.setStarting(current, false);
        // e.g. entitlement lapsed or the definition changed: the server says why
        const message = (error && (error.error || error.message)) || "Could not start regeneration";
        this.snackBar.open(typeof message === "string" ? message : "Could not start regeneration", null, { duration: 8000 });
        this.loadLayouts();
      },
    });
    return false;
  }

  private setStarting(layout: AdmLayoutDetails | undefined, starting: boolean) {
    if (!layout) return;
    if (starting) this.startingIds.add(layout.admLayoutId); else this.startingIds.delete(layout.admLayoutId);
    layout.statusText = this.getStatusText(layout);
  }

  deleteLayout(layout: AdmLayoutEx) {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Confirm Delete",
      "Are you sure you want to delete this Agent Data Model? Its data and its access credentials will be removed."
    ).subscribe((result) => {
      if (result.ResultType === DialogResultType.Ok) {
        this.app.pushLoading("Deleting, Please Wait...");
        this.admLayoutService.admLayoutDeleteLayout(layout.admLayoutId).subscribe(() => {
          this.app.popLoading();
          this.loadLayouts();
        });
      }
    });
    return false;
  }

  showConnectionInfo(layout: AdmLayoutEx) {
    AdmConnectionDialogComponent.showDialog(this.dialog, `Connection Info: ${layout.name}`, { admLayoutId: layout.admLayoutId }).subscribe();
    return false;
  }

  showSubscriptionConnectionInfo(subscription: AdmSubscriptionLite) {
    const name = subscription.subscriptionName || subscription.organizationName || "Subscription";
    AdmConnectionDialogComponent.showDialog(this.dialog, `All Agent Data Models: ${name}`, { subscriptionId: subscription.subscriptionId }).subscribe();
    return false;
  }
}
